import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseQuoteToolModeService, QuoteToolStrategyService } from '@lx/quote-tool/shared/services';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { RouteIds } from '../../types';
import { NavigationService } from '../navigation.service';
import { DefaultModeStrategyService, OnlineCourseModeStrategyService } from './strategies';

/**
 * Online Quote Tool Mode Service
 *
 * This service implements the strategy pattern to handle different initialization modes:
 * - Default: Load from session storage or start fresh
 * - Online Course: Load from deep link with pre-filled course data
 *
 * Responsibilities:
 * 1. Determine which strategy to use based on query parameters
 * 2. Load data via the selected strategy
 * 3. Navigate to the appropriate step
 *
 * Architecture:
 * - Extends BaseQuoteToolModeService for shared mode logic
 * - Composes strategies for different initialization scenarios
 * - Delegates navigation to NavigationService
 *
 * Usage Flow:
 * 1. APP_INITIALIZER calls loadData$() to load strategy-specific data
 * 2. AppComponent.ngOnInit calls navigate() to perform navigation
 *
 * @example
 * // In APP_INITIALIZER
 * modeService.loadData$().subscribe(loaded => {
 *   if (loaded) {
 *     // Store initialized, app ready to bootstrap
 *   }
 * });
 *
 * @example
 * // In AppComponent
 * modeService.navigate().subscribe(success => {
 *   // Navigation completed
 * });
 */
@Injectable({
  providedIn: 'root',
})
export class OnlineQuoteToolModeService extends BaseQuoteToolModeService<RouteIds> {
  private readonly navigationService = inject(NavigationService);
  private readonly defaultModeStrategy = inject(DefaultModeStrategyService);
  private readonly onlineCourseModeStrategy = inject(OnlineCourseModeStrategyService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();

    // Register available strategies
    // Only default and online-course modes are supported
    this.strategies = {
      default: this.defaultModeStrategy,
      'online-course': this.onlineCourseModeStrategy,
    };
  }

  /**
   * Loads data for the selected strategy
   *
   * Flow:
   * 1. Get strategy based on query parameters
   * 2. Call strategy's loadData$() method
   * 3. If loading fails, fallback to default strategy
   * 4. Reset query parameters on error to prevent retry loops
   *
   * @returns Observable<boolean> - true if data loaded successfully, false otherwise
   */
  loadData$(): Observable<boolean> {
    return this.getStrategy()
      .loadData$()
      .pipe(
        switchMap((loaded) => {
          if (!loaded) {
            this.logger.error(`Error loading data for quote tool mode: ${this.hostDataReader.getQuoteToolMode()}. Loading default data.`);

            // Reset query parameters to prevent retry with bad data
            this.hostDataReader.resetQueryParameters();

            // Fallback to default strategy
            return this.strategies.default.loadData$();
          }
          return of(loaded);
        }),
        takeUntilDestroyed(this.destroyRef),
      );
  }

  /**
   * Navigates to the appropriate step based on the selected strategy
   *
   * Flow:
   * 1. Get strategy based on query parameters (or default)
   * 2. Get step to navigate to from strategy
   * 3. Execute navigation via NavigationService
   * 4. Emit completion signal
   *
   * @returns Observable<boolean> - true when navigation completes
   */
  navigate(): Observable<boolean> {
    const strategy: QuoteToolStrategyService<RouteIds> = this.getStrategy();
    this.doNavigation(strategy);
    return this.navigationCompleted$.asObservable();
  }

  /**
   * Executes navigation based on the strategy
   *
   * Handles both Observable and direct RouteIds return types from strategies.
   * Updates navigationCompleted$ when navigation finishes.
   *
   * @param strategy - The strategy containing navigation logic
   * @protected
   */
  protected doNavigation(strategy: QuoteToolStrategyService<RouteIds>): void {
    const stepToNavigate = strategy.getStepToNavigate();
    const stepToNavigate$ = stepToNavigate instanceof Observable ? stepToNavigate : of(stepToNavigate);
    stepToNavigate$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((routeId) => {
      this.navigationService.navigateToStep(routeId, () => {
        this.navigationCompleted$.next(true);
      });
    });
  }

  /**
   * Navigates to the default (first) step
   *
   * Called when no valid strategy or step is found.
   * @protected
   */
  protected navigateToDefault(): void {
    this.navigationService.navigateToFirstStep(() => {
      this.navigationCompleted$.next(true);
    });
  }
}
